import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Library, LogOut, Search } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { logout } from '../api/auth';
import { GlobalSearch } from './GlobalSearch';

export function Navigation() {
  const [open, setOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    await logout();
    clearAuth();
    navigate('/login');
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      <div className="nav-brand">
        <Link to="/" aria-label="Library home">
          <Library className="icon icon-md" aria-hidden="true" />
          <span>Library</span>
        </Link>
      </div>

      <div className="nav-search-desktop">
        <GlobalSearch />
      </div>

      <button
        className="nav-search-mobile-trigger"
        onClick={() => setMobileSearchOpen(true)}
        aria-label="Open search"
      >
        <Search className="icon icon-sm" aria-hidden="true" />
      </button>

      {mobileSearchOpen && (
        <GlobalSearch
          mobileOpen
          onMobileClose={() => setMobileSearchOpen(false)}
        />
      )}

      <button
        className={`nav-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Toggle navigation menu"
        aria-controls="nav-menu"
      >
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </button>

      <div
        id="nav-menu"
        className={`nav-links ${open ? 'open' : ''}`}
        onClick={() => setOpen(false)}
        role="menu"
      >
        <Link
          to="/books"
          className={isActive('/books') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/books') ? 'page' : undefined}
        >
          Books
        </Link>
        <Link
          to="/videos"
          className={isActive('/videos') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/videos') ? 'page' : undefined}
        >
          Videos
        </Link>
        <Link
          to="/audio"
          className={isActive('/audio') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/audio') ? 'page' : undefined}
        >
          Audio
        </Link>
        <Link
          to="/authors"
          className={isActive('/authors') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/authors') ? 'page' : undefined}
        >
          Authors
        </Link>
        <Link
          to="/lists"
          className={isActive('/lists') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/lists') ? 'page' : undefined}
        >
          Collections
        </Link>
        <Link
          to="/upload"
          className={isActive('/upload') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/upload') ? 'page' : undefined}
        >
          Upload
        </Link>
        <Link
          to="/chat"
          className={isActive('/chat') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/chat') ? 'page' : undefined}
        >
          Librarian
        </Link>
        <Link
          to="/saved"
          className={isActive('/saved') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/saved') ? 'page' : undefined}
        >
          Saved
        </Link>
        <Link
          to="/admin"
          className={isActive('/admin') ? 'active' : ''}
          role="menuitem"
          aria-current={isActive('/admin') ? 'page' : undefined}
        >
          Admin
        </Link>
        <div className="nav-user">
          <Link to="/settings" className="nav-username-link" aria-label="Settings">
            {user?.displayName || user?.username}
          </Link>
          <button
            className="nav-logout"
            onClick={handleLogout}
            aria-label="Sign out"
          >
            <LogOut className="icon icon-sm" />
          </button>
        </div>
      </div>
    </nav>
  );
}
